/**
 * Presentation-only citation labels for Cognitory MVP answers.
 *
 * Labels are UI metadata, not evidence. Full citation identity remains on the
 * response object for audit. Titles come only from governed document metadata
 * or bounded LTDD fixture allowlist entries — never invented from storage paths.
 */

export type CognitoryCitationIdentity = {
  citationLabel: string;
  documentId: string | null;
  documentVersionId: string | null;
  chunkId: string | null;
  sourcePath: string | null;
  sourceCharStart: number | null;
  sourceCharEnd: number | null;
  evidenceSpanStart: number | null;
  evidenceSpanEnd: number | null;
  verificationReference: string | null;
};

/**
 * Bounded approved display titles keyed by governed documentId.
 * Source: LTDD Operations Brief fixture / locked document identity
 * (`doc_ltdd_operations_brief`). User-facing label is presentation-only.
 */
export const APPROVED_CITATION_DOCUMENT_TITLES: Readonly<
  Record<string, string>
> = {
  doc_ltdd_operations_brief: "LTDD Operations Brief",
};

export const FALLBACK_CITATION_DOCUMENT_LABEL = "Cited document" as const;

export type CognitoryCitationTitleLookup = Readonly<
  Record<string, string | null | undefined>
>;

/** Extract a calm citation number from labels like `[^1]` or `[1]`. */
export function citationDisplayNumber(citationLabel: string): string {
  const match = citationLabel.match(/(\d+)/);
  return match?.[1] ?? citationLabel;
}

/**
 * Resolve a user-facing document label without reading titles from paths.
 */
export function resolveCitationDocumentLabel(
  citation: Pick<CognitoryCitationIdentity, "documentId">,
  governedTitles?: CognitoryCitationTitleLookup,
): string {
  const documentId = citation.documentId;
  if (documentId) {
    const governedTitle = governedTitles?.[documentId]?.trim();
    if (governedTitle) {
      return governedTitle;
    }
    if (APPROVED_CITATION_DOCUMENT_TITLES[documentId]) {
      return APPROVED_CITATION_DOCUMENT_TITLES[documentId];
    }
  }
  return FALLBACK_CITATION_DOCUMENT_LABEL;
}

export function formatCitationListLabel(
  citation: CognitoryCitationIdentity,
  governedTitles?: CognitoryCitationTitleLookup,
): string {
  const number = citationDisplayNumber(citation.citationLabel);
  const title = resolveCitationDocumentLabel(citation, governedTitles);
  return `[${number}] ${title}`;
}

export function buildCitationTitleLookupFromLibraryDocuments(
  documents: ReadonlyArray<{ documentId: string; title: string }>,
): CognitoryCitationTitleLookup {
  const lookup: Record<string, string> = {};
  for (const document of documents) {
    const title = document.title.trim();
    if (title.length > 0) {
      lookup[document.documentId] = title;
    }
  }
  return lookup;
}

export function findCitationByLabel(
  citations: readonly CognitoryCitationIdentity[],
  citationLabel: string,
): CognitoryCitationIdentity | null {
  return (
    citations.find(citation => citation.citationLabel === citationLabel) ?? null
  );
}

export function findCitationByDisplayNumber(
  citations: readonly CognitoryCitationIdentity[],
  displayNumber: string,
): CognitoryCitationIdentity | null {
  return (
    citations.find(
      citation =>
        citationDisplayNumber(citation.citationLabel) === displayNumber,
    ) ?? null
  );
}
